import React, { useState } from 'react';
import { 
  Tabs, 
  Tab, 
  Box, 
  Typography, 
  useTheme, 
  Chip, 
  Alert, 
  Paper, 
  Container, 
  Card,
  CardContent,
  CardMedia,
  Grid,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import MuscleHighlighter from '../components/MuscleHighlighter';
import { getMusclesForExercise } from '../utils/muscleMapping';
import { getCombinedWorkoutMuscles } from '../utils/workoutMuscleUtils';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import TimerIcon from '@mui/icons-material/Timer';
import SpeedIcon from '@mui/icons-material/Speed';
import InfoIcon from '@mui/icons-material/Info';
import { SportsMma, SportsBaseball,SportsTennis } from '@mui/icons-material';
import PoolIcon from '@mui/icons-material/Pool';
import SelfImprovementIcon from '@mui/icons-material/SelfImprovement';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AnatomyIcon from '@mui/icons-material/Accessibility';
import '../App.css'; // Assuming App.css has some basic styling or global styles
import '../pages/Workout.css'; // Import the new Workout.css

// Helper component for Tab Panel content
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`workout-tabpanel-${index}`}
      aria-labelledby={`workout-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography component="div">{children}</Typography>
        </Box>
      )}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `workout-tab-${index}`,
    'aria-controls': `workout-tabpanel-${index}`,
  };
}

// Enhanced Exercise Card Component with Muscle Highlighting
const ExerciseCard = ({ exercise }) => {
  const theme = useTheme();
  
  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardMedia
        component="img"
        height="300"
        image={exercise?.image || '/images/profile.jpg'}
        alt={exercise?.name || 'Exercise'}
      />
      <CardContent sx={{ flexGrow: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="h6" sx={{ color: theme.palette.primary.main, flexGrow: 1 }}>
            {exercise?.name || 'Exercise'}
          </Typography>
          <Tooltip title="Exercise Information">
            <IconButton size="small" sx={{ color: theme.palette.info.main }}>
              <InfoIcon />
            </IconButton>
          </Tooltip>
        </Box>
        
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
            {exercise?.sets || '-'} sets × {exercise?.reps || '-'} reps
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Rest: {exercise?.rest || '-'}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {exercise?.description || ''}
          </Typography>
        </Box>
        
        {/* Muscle Groups */}
        {Array.isArray(exercise?.muscles) && exercise.muscles.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', mb: 1 }}>
              <AnatomyIcon sx={{ mr: 0.5, fontSize: '1rem' }} />
              Target Muscles:
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {exercise.muscles.map((muscle, index) => (
                <Chip key={index} label={muscle} size="small" variant="outlined" />
              ))}
            </Box>
          </Box>
        )}
        
        {/* Expandable Details */}
        <Accordion elevation={0} sx={{ boxShadow: 'none', border: '1px solid', borderColor: 'divider' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Exercise Details</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {/* Proper Form */}
            {Array.isArray(exercise?.properForm) && exercise.properForm.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CheckCircleIcon sx={{ mr: 0.5, fontSize: '1rem', color: 'success.main' }} />
                  Proper Form:
                </Typography>
                <List dense>
                  {exercise.properForm.map((tip, index) => (
                    <ListItem key={index} sx={{ py: 0.25 }}>
                      <ListItemText primary={tip} primaryTypographyProps={{ variant: 'body2' }} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
            
            <Divider sx={{ my: 1 }} />
            
            {/* Easier Variation */}
            {exercise?.easierVariation && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', mb: 1 }}>
                  <TrendingDownIcon sx={{ mr: 0.5, fontSize: '1rem', color: 'info.main' }} />
                  Easier Variation:
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {exercise.easierVariation.name}
                </Typography>
                {exercise.easierVariation.description && (
                  <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                    {exercise.easierVariation.description}
                  </Typography>
                )}
              </Box>
            )}
            
            <Divider sx={{ my: 1 }} />
            
            {/* Common Mistakes */}
            {Array.isArray(exercise?.commonMistakes) && exercise.commonMistakes.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', mb: 1 }}>
                  <WarningIcon sx={{ mr: 0.5, fontSize: '1rem', color: 'warning.main' }} />
                  Common Mistakes:
                </Typography>
                <List dense>
                  {exercise.commonMistakes.map((mistake, index) => (
                    <ListItem key={index} sx={{ py: 0.25 }}>
                      <ListItemText primary={mistake} primaryTypographyProps={{ variant: 'body2' }} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      </CardContent>
    </Card>
  );
};

// Exercise Data with Enhanced Information
const fridayExercises = [
  {
    name: "Overhead Press (Standing)",
    image: "/images/overhead-press-standing.gif",
    sets: "4",
    reps: "8-10",
    rest: "2-3 minutes",
    description: "Overall shoulder mass, compound.",
    muscles: ["Deltoids", "Triceps", "Upper Chest", "Core"],
    properForm: ["Straight bar path", "Tight core", "No over-arching"]
  },
  {
    name: "Lateral Raises",
    image: "/images/profile.jpg",
    sets: "4",
    reps: "12-15",
    rest: "90 seconds",
    description: "Side delt isolation, shoulder width.",
    muscles: ["Medial Deltoids"],
    properForm: ["Soft elbows", "Raise to shoulder height", "Lead with elbows"]
  },
  {
    name: "Rear Delt Flyes",
    image: "/images/profile.jpg",
    sets: "4",
    reps: "12-15",
    rest: "90 seconds",
    description: "Rear delt isolation, posture.",
    muscles: ["Rear Deltoids"],
    properForm: ["Hinge posture", "Arms in Y/T path", "Squeeze rear delts"]
  },
  {
    name: "Arnold Press",
    image: "/images/Dumbbell-Push-Press.gif",
    sets: "3",
    reps: "10-12",
    rest: "90 seconds",
    description: "Full ROM shoulder development.",
    muscles: ["Deltoids", "Triceps"],
    properForm: ["Rotate and press", "Control through ROM", "No torso sway"]
  },
  {
    name: "Upright Rows",
    image: "/images/profile.jpg",
    sets: "3",
    reps: "10-12",
    rest: "90 seconds",
    description: "Trap/side delt, controlled movement.",
    muscles: ["Upper Traps", "Medial Deltoids"],
    properForm: ["Elbows lead", "Bar close", "No wrist strain"]
  },
  {
    name: "Barbell Curls (21s)",
    image: "/images/profile.jpg",
    sets: "3",
    reps: "21 (7+7+7)",
    rest: "2 minutes",
    description: "Biceps specialization technique.",
    muscles: ["Biceps"],
    properForm: ["Strict reps", "No swing", "Squeeze each portion"]
  },
  {
    name: "Close-Grip Push-ups",
    image: "/images/profile.jpg",
    sets: "3",
    reps: "12-15",
    rest: "90 seconds",
    description: "Triceps endurance, bodyweight.",
    muscles: ["Triceps", "Chest"],
    properForm: ["Hands close", "Elbows tight", "Full ROM"]
  },
  {
    name: "Alternating Dumbbell Curls",
    image: "/images/profile.jpg",
    sets: "3",
    reps: "12-15 each",
    rest: "90 seconds",
    description: "Biceps pump, unilateral focus.",
    muscles: ["Biceps"],
    properForm: ["Elbows stationary", "Controlled tempo", "Squeeze at top"]
  },
  {
    name: "Triceps Dips",
    image: "/images/profile.jpg",
    sets: "3",
    reps: "10-15",
    rest: "90 seconds",
    description: "Triceps compound, bodyweight.",
    muscles: ["Triceps", "Chest"],
    properForm: ["Shoulders down", "Full depth", "Lockout controlled"]
  },
  {
    name: "Wrist Curls",
    image: "/images/profile.jpg",
    sets: "3",
    reps: "15-20",
    rest: "60 seconds",
    description: "Forearm flexors, grip strength.",
    muscles: ["Forearms"],
    properForm: ["Full flexion", "Slow eccentric", "Neutral forearm position"]
  },
  {
    name: "Reverse Wrist Curls",
    image: "/images/profile.jpg",
    sets: "3",
    reps: "15-20",
    rest: "60 seconds",
    description: "Forearm extensors, balance.",
    muscles: ["Forearms"],
    properForm: ["Full extension", "Slow eccentric", "Controlled range"]
  }
];

const wednesdayExercises = [
  {
    name: "Deadlifts (Conventional)",
    image: "/images/conventional-deadlift.webp",
    sets: "4",
    reps: "5-6",
    rest: "3-4 minutes",
    description: "Overall back strength, full body power.",
    muscles: ["Hamstrings", "Glutes", "Lower Back", "Traps", "Core", "Forearms"],
    properForm: ["Bar over mid-foot", "Tight lats", "Drive through heels", "Neutral spine"]
  },
  {
    name: "Wide-Grip Pull-ups/Lat Pulldowns",
    image: "/images/weighted-pull-ups.gif",
    sets: "4",
    reps: "8-12",
    rest: "2-3 minutes",
    description: "Lat width, V-taper development.",
    muscles: ["Latissimus Dorsi", "Teres Major", "Biceps", "Rear Deltoids"],
    properForm: ["Full stretch", "Lead with elbows", "Chest to bar/handle"]
  },
  {
    name: "Barbell Rows",
    image: "/images/barbell-rows.gif",
    sets: "4",
    reps: "8-10",
    rest: "2-3 minutes",
    description: "Mid-back thickness, posture.",
    muscles: ["Latissimus Dorsi", "Rhomboids", "Traps", "Rear Deltoids"],
    properForm: ["Hinge maintained", "Row to lower chest", "Squeeze scapulae"]
  },
  {
    name: "Seated Cable Rows",
    image: "/images/profile.jpg",
    sets: "3",
    reps: "10-12",
    rest: "90 seconds",
    description: "Lower lat isolation, mid-back.",
    muscles: ["Latissimus Dorsi", "Rhomboids", "Rear Deltoids"],
    properForm: ["Neutral spine", "Pull to midline", "Controlled eccentric"]
  },
  {
    name: "Face Pulls",
    image: "/images/profile.jpg",
    sets: "3",
    reps: "12-15",
    rest: "90 seconds",
    description: "Rear delts, posture correction.",
    muscles: ["Rear Deltoids", "Rhomboids", "Lower Traps"],
    properForm: ["Elbows high", "Externally rotate", "Squeeze rear delts"]
  },
  {
    name: "Barbell Biceps Curls",
    image: "/images/profile.jpg",
    sets: "4",
    reps: "8-10",
    rest: "2 minutes",
    description: "Biceps mass, heavy compound.",
    muscles: ["Biceps"],
    properForm: ["Elbows stationary", "No torso swing", "Full ROM"]
  },
  {
    name: "Hammer Curls",
    image: "/images/profile.jpg",
    sets: "3",
    reps: "10-12",
    rest: "90 seconds",
    description: "Brachialis, forearm involvement.",
    muscles: ["Biceps", "Forearms"],
    properForm: ["Neutral grip", "Controlled tempo", "Squeeze at top"]
  },
  {
    name: "Cable Biceps Curls",
    image: "/images/profile.jpg",
    sets: "3",
    reps: "12-15",
    rest: "90 seconds",
    description: "Biceps pump, constant tension.",
    muscles: ["Biceps"],
    properForm: ["Elbows down", "No shoulder sway", "Slow eccentric"]
  },
  {
    name: "Farmer's Walks",
    image: "/images/profile.jpg",
    sets: "3",
    reps: "30-45 seconds",
    rest: "90 seconds",
    description: "Forearms, grip strength, functional.",
    muscles: ["Forearms", "Grip", "Core"],
    properForm: ["Upright posture", "Tight core", "Controlled steps"]
  }
];

const mondayExercises = [
  {
    name: "Barbell Back Squats",
    image: "/images/barbell-back-squats.gif",
    sets: "4",
    reps: "8-10",
    rest: "2-3 minutes between sets",
    description: "King of all exercises. Builds overall leg strength and power.",
    muscles: ["Quadriceps", "Glutes", "Hamstrings", "Core"],
    properForm: [
      "Keep your chest up and core tight throughout the movement",
      "Descend until thighs are parallel to the floor",
      "Drive through your heels to return to starting position",
      "Keep knees tracking over toes, don't let them cave inward"
    ],
    easierVariation: {
      name: "Goblet Squats",
      description: "Hold a dumbbell or kettlebell at chest level. Easier to maintain proper form and requires less mobility."
    },
    commonMistakes: [
      "Knees caving inward during the movement",
      "Not going deep enough - aim for thighs parallel to floor",
      "Leaning too far forward, losing chest position",
      "Rising on toes instead of driving through heels"
    ]
  },
  {
    name: "Incline Dumbbell Press",
    image: "/images/incline-dumbbell-press.gif",
    sets: "4",
    reps: "10-12",
    rest: "90 seconds between sets",
    description: "Upper chest and shoulder development. Control the weight, full range of motion.",
    muscles: ["Upper Chest", "Anterior Deltoids", "Triceps"],
    properForm: [
      "Set bench to 30-45 degree incline",
      "Lower dumbbells with control until you feel a stretch in chest",
      "Press up and slightly inward, bringing dumbbells together at top",
      "Keep your core engaged and back pressed against bench"
    ],
    easierVariation: {
      name: "Incline Push-ups",
      description: "Place hands on an elevated surface like a bench. The higher the surface, the easier the exercise."
    },
    commonMistakes: [
      "Using too steep of an incline (over 45 degrees)",
      "Bouncing the weight off the chest",
      "Not maintaining control during the lowering phase",
      "Arching back excessively off the bench"
    ]
  },
  {
    name: "Bent-Over Barbell Rows",
    image: "/images/Barbell-Bent-Over-Row.gif",
    sets: "4",
    reps: "10",
    rest: "90 seconds between sets",
    description: "Mid-back thickness. Pull to lower chest, squeeze shoulder blades.",
    muscles: ["Latissimus Dorsi", "Rhomboids", "Middle Traps", "Rear Deltoids"],
    properForm: [
      "Hinge at hips with slight knee bend, keep back straight",
      "Pull bar to lower chest/upper abdomen",
      "Squeeze shoulder blades together at the top",
      "Control the weight on the way down"
    ],
    easierVariation: {
      name: "Seated Cable Rows",
      description: "Provides back support and allows better control of the movement. Great for learning the pulling pattern."
    },
    commonMistakes: [
      "Rounding the back during the movement",
      "Using momentum to swing the weight up",
      "Not squeezing shoulder blades together",
      "Standing too upright, reducing back engagement"
    ]
  },
  {
    name: "Overhead Press (Standing)",
    image: "/images/overhead-press-standing.gif",
    sets: "3",
    reps: "12",
    rest: "75 seconds between sets",
    description: "Shoulder and core strength. Maintain tight core, avoid back arch.",
    muscles: ["Shoulders", "Triceps", "Upper Chest", "Core"],
    properForm: [
      "Start with bar at shoulder level, hands slightly wider than shoulders",
      "Press straight up, keeping core tight",
      "Lock out arms overhead without arching back",
      "Lower with control back to starting position"
    ],
    easierVariation: {
      name: "Seated Dumbbell Press",
      description: "Seated position provides back support and removes core stability requirement, allowing focus on shoulder strength."
    },
    commonMistakes: [
      "Arching back excessively to help lift the weight",
      "Pressing the bar forward instead of straight up",
      "Not engaging core muscles for stability",
      "Using legs to help drive the weight up"
    ]
  },
  {
    name: "Romanian Deadlifts",
    image: "/images/romanian-deadlift.webp",
    sets: "3",
    reps: "12",
    rest: "75 seconds between sets",
    description: "Hamstring and glute development. Hip hinge pattern, feel the stretch.",
    muscles: ["Hamstrings", "Glutes", "Lower Back", "Core"],
    properForm: [
      "Start with bar at hip level, slight knee bend",
      "Push hips back while lowering bar along legs",
      "Feel stretch in hamstrings, then drive hips forward",
      "Keep bar close to body throughout movement"
    ],
    easierVariation: {
      name: "Dumbbell Romanian Deadlifts",
      description: "Using dumbbells allows for better range of motion and is easier to control than a barbell."
    },
    commonMistakes: [
      "Squatting down instead of hinging at hips",
      "Rounding the back during the movement",
      "Not feeling the stretch in hamstrings",
      "Letting the bar drift away from the body"
    ]
  },
  {
    name: "Weighted Dips",
    image: "/images/weighted-dips.gif",
    sets: "3",
    reps: "10-12",
    rest: "60 seconds between sets",
    description: "Lower chest and tricep mass. Lean forward slightly for chest emphasis.",
    muscles: ["Lower Chest", "Triceps", "Anterior Deltoids"],
    properForm: [
      "Lean slightly forward to target chest more",
      "Lower until shoulders are below elbows",
      "Press up with control, don't lock out aggressively",
      "Keep core engaged throughout movement"
    ],
    easierVariation: {
      name: "Assisted Dips",
      description: "Use an assisted dip machine or resistance bands to reduce body weight. Focus on form before adding weight."
    },
    commonMistakes: [
      "Not going deep enough in the bottom position",
      "Staying too upright, making it more of a tricep exercise",
      "Swinging or using momentum",
      "Shrugging shoulders up toward ears"
    ]
  },
  {
    name: "Plank Variations",
    image: "/images/plank.gif",
    sets: "3",
    reps: "45-60 seconds",
    rest: "60 seconds between sets",
    description: "Core stability and strength. Maintain straight line from head to heels.",
    muscles: ["Core", "Shoulders", "Glutes"],
    properForm: [
      "Maintain straight line from head to heels",
      "Keep core engaged throughout the hold",
      "Don't let hips sag or pike up",
      "Breathe normally, don't hold your breath"
    ],
    easierVariation: {
      name: "Knee Plank",
      description: "Drop to knees while maintaining straight line from head to knees. Reduces load while building core strength."
    },
    commonMistakes: [
      "Letting hips sag toward the ground",
      "Piking hips up too high",
      "Holding breath during the exercise",
      "Placing hands too far forward or back"
    ]
  }
];

const MondayWorkout = () => (
  <section className="workout-day">
    <Box sx={{ mb: 3, p: 2, bgcolor: 'rgba(156, 39, 176, 0.08)', borderRadius: 2, border: '1px solid rgba(156, 39, 176, 0.2)' }}>
      <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', color: 'secondary.main', mb: 2 }}>
        Monday - Boxing Bag Workout + Active Recovery <Chip label="45-60 min" size="small" sx={{ ml: 1 }} /> <Chip label="Moderate" size="small" color="secondary" sx={{ ml: 1 }} />
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        <strong>Focus:</strong> Cardio, Coordination, Upper Body Recovery<br/>
        <strong>Intensity:</strong> Moderate
      </Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>
        Active recovery session focusing on cardiovascular health, coordination, and stress relief through boxing training.
        This session promotes blood flow and aids recovery while maintaining fitness momentum.
      </Typography>
    </Box>
    
    {/* Boxing Session */}
    <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
      <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center' }}>
        <SportsMma sx={{ mr: 1, color: 'secondary.main' }} /> Boxing Session (30-45 minutes):
      </Typography>
      
      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, color: 'secondary.main' }}>Warm-up (5 minutes):</Typography>
      <Box component="ul" sx={{ pl: 2, mb: 2 }}>
        <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>Jumping jacks</Typography>
        <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>Arm circles</Typography>
        <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>Shadow boxing</Typography>
      </Box>
      
      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, color: 'secondary.main' }}>Bag Work (20-30 minutes):</Typography>
      <Box component="ul" sx={{ pl: 2, mb: 2 }}>
        <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>3-minute rounds with 1-minute rest</Typography>
        <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>Focus on: jab-cross combinations, hooks, uppercuts</Typography>
        <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>Include footwork and defensive movements</Typography>
      </Box>
      
      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, color: 'secondary.main' }}>Technique Focus:</Typography>
      <Box component="ul" sx={{ pl: 2, mb: 0 }}>
        <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>Proper punching form</Typography>
        <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>Breathing rhythm</Typography>
      </Box>
    </Paper>
    
    {/* Active Recovery */}
    <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
      <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center' }}>
        <TimerIcon sx={{ mr: 1, color: 'secondary.main' }} /> Active Recovery (15 minutes):
      </Typography>
      <Box component="ul" sx={{ pl: 2, mb: 0 }}>
        <Typography component="li" variant="body2" sx={{ mb: 1 }}>Light walking or gentle stretching</Typography>
        <Typography component="li" variant="body2" sx={{ mb: 1 }}>Foam rolling for shoulders, chest, and upper back</Typography>
        <Typography component="li" variant="body2">Breathing exercises</Typography>
      </Box>
    </Paper>
    
    <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center' }}>
      <SportsMma sx={{ mr: 1, color: 'secondary.main' }} /> Focus Areas:
    </Typography>
    
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <Card elevation={2} sx={{ height: '100%', borderRadius: 2 }}>
          <CardContent>
            <Typography variant="h6" component="h4" sx={{ fontWeight: 'bold', mb: 1, display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
              Cardiovascular Conditioning
              <Chip label="Cardio" size="small" color="secondary" sx={{ ml: 1 }} />
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Improve heart health and endurance through sustained moderate-intensity movement
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      
      <Grid item xs={12} md={6}>
        <Card elevation={2} sx={{ height: '100%', borderRadius: 2 }}>
          <CardContent>
            <Typography variant="h6" component="h4" sx={{ fontWeight: 'bold', mb: 1, display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
              Coordination & Agility
              <Chip label="Skill" size="small" color="info" sx={{ ml: 1 }} />
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Enhance hand-eye coordination, footwork, and reaction time
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      
      <Grid item xs={12}>
        <Alert severity="info" sx={{ mt: 1 }}>
          <Typography variant="body2">
            <strong>Recovery Focus:</strong> Keep intensity moderate to promote recovery from previous strength sessions while maintaining activity levels.
          </Typography>
        </Alert>
      </Grid>
    </Grid>
  </section>
);

const WednesdayWorkout = () => {
  const theme = useTheme();
  
  // Get all muscles worked in Wednesday's workout
  const { svgMuscles, displayNames, allMuscles: allWednesdayMuscles } = getCombinedWorkoutMuscles(wednesdayExercises);
  
  return (
    <div>
      <Typography variant="h5" sx={{ mb: 2, color: theme.palette.primary.main, fontWeight: 'bold' }}>
        Wednesday - Back & Biceps
      </Typography>
      

      
      {/* Session Details */}
      <Card sx={{ mb: 3, backgroundColor: theme.palette.mode === 'dark' ? '#2a2a2a' : '#f8f9fa' }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, color: theme.palette.primary.main }}>
            📋 Session Overview
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Duration:</strong> 75-90 minutes
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Focus:</strong> Maximum Strength & Neural Adaptations
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Intensity:</strong> Very High (85-90% 1RM, 3-6 reps)
          </Typography>
          <Typography variant="body2">
            <strong>Equipment:</strong> Barbell, squat rack, bench, pull-up bar, deadlift platform
          </Typography>
        </CardContent>
      </Card>
      
      {/* Warm-up Section */}
      <Card sx={{ mb: 3, backgroundColor: theme.palette.mode === 'dark' ? '#2a2a2a' : '#f8f9fa' }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, color: theme.palette.primary.main }}>
            🔥 Dynamic Warm-up (12 minutes)
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            • 5 minutes moderate cardio
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            • Dynamic mobility work
          </Typography>
          <Typography variant="body2">
            • Progressive loading: empty bar → 50% → 70% → working weight
          </Typography>
        </CardContent>
      </Card>

      {/* Main Workout */}
      <Grid container spacing={3}>
        {wednesdayExercises.map((exercise, index) => (
          <Grid item xs={12} md={6} key={index}>
            <ExerciseCard exercise={exercise} />
          </Grid>
        ))}
      </Grid>

      {/* Muscle Visualization Section */}
      <Card sx={{ mt: 3, mb: 3, backgroundColor: theme.palette.mode === 'dark' ? '#2a2a2a' : '#f0f8ff' }}>
        <CardContent>
          <Typography variant="h6" sx={{ color: theme.palette.primary.main, mb: 2 }}>
            💪 Muscles Targeted Today
          </Typography>
          
          <Box sx={{ mt: 2 }}>
            <MuscleHighlighter
              primaryMuscles={svgMuscles.primary}
              secondaryMuscles={svgMuscles.secondary}
              showTooltips={true}
              size="medium"
              exercises={wednesdayExercises}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Cool-down Section */}
      <Card sx={{ mt: 3, backgroundColor: theme.palette.mode === 'dark' ? '#2a2a2a' : '#f0f8ff' }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, color: theme.palette.primary.main }}>
            🧘 Cool-down & Stretching (5-10 minutes)
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            • Chest doorway stretch (30 seconds each arm)
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            • Overhead tricep stretch (30 seconds each arm)
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            • Hip flexor stretch (30 seconds each leg)
          </Typography>
          <Typography variant="body2">
            • General mobility work and light stretching
          </Typography>
        </CardContent>
      </Card>
    </div>
  );
};

const FridayWorkout = () => {
  const theme = useTheme();
  
  // Get all muscles worked in Friday's workout
  const { svgMuscles, displayNames, allMuscles: allFridayMuscles } = getCombinedWorkoutMuscles(fridayExercises);
  
  return (
    <div>
      <Typography variant="h5" sx={{ mb: 2, color: theme.palette.primary.main, fontWeight: 'bold' }}>
        Friday - Shoulders & Arms
      </Typography>
      

      
      {/* Session Details */}
      <Card sx={{ mb: 3, backgroundColor: theme.palette.mode === 'dark' ? '#2a2a2a' : '#f8f9fa' }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, color: theme.palette.primary.main }}>
            📋 Session Overview
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Duration:</strong> 60-75 minutes
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Focus:</strong> Shoulder Strength & Arm Hypertrophy
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Intensity:</strong> Moderate-High (8-12 reps)
          </Typography>
          <Typography variant="body2">
            <strong>Equipment:</strong> Barbell, dumbbells, cables, bench
          </Typography>
        </CardContent>
      </Card>
      
      {/* Warm-up Section */}
      <Card sx={{ mb: 3, backgroundColor: theme.palette.mode === 'dark' ? '#2a2a2a' : '#f8f9fa' }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, color: theme.palette.primary.main }}>
            🔥 Dynamic Warm-up (8-10 minutes)
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            • 5 minutes light cardio
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            • Shoulder mobility: band dislocates, arm circles
          </Typography>
          <Typography variant="body2">
            • Scapular activation: face-pull patterning
          </Typography>
        </CardContent>
      </Card>
    
      {/* Main Workout */}
      <Grid container spacing={3}>
        {fridayExercises.map((exercise, index) => (
          <Grid item xs={12} md={6} key={index}>
            <ExerciseCard exercise={exercise} />
          </Grid>
        ))}
      </Grid>

      {/* Muscle Visualization Section */}
      <Card sx={{ mt: 3, mb: 3, backgroundColor: theme.palette.mode === 'dark' ? '#2a2a2a' : '#f0f8ff' }}>
        <CardContent>
          <Typography variant="h6" sx={{ color: theme.palette.primary.main, mb: 2 }}>
            💪 Muscles Targeted Today
          </Typography>
          
          <Box sx={{ mt: 2 }}>
            <MuscleHighlighter
              primaryMuscles={svgMuscles.primary}
              secondaryMuscles={svgMuscles.secondary}
              exercises={fridayExercises}
              showLegend={true}
              showTooltips={true}
              height="300px"
            />
          </Box>
        </CardContent>
      </Card>

      {/* Cool-down Section */}
      <Card sx={{ mt: 3, backgroundColor: theme.palette.mode === 'dark' ? '#2a2a2a' : '#f0f8ff' }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, color: theme.palette.primary.main }}>
            🧘 Cool-down (5-10 minutes)
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            • Light walking or easy movement
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            • Dynamic stretching for worked muscles
          </Typography>
          <Typography variant="body2">
            • Deep breathing and heart rate recovery
          </Typography>
          <Typography variant="body2">
            • Hydration and nutrition planning for recovery
          </Typography>
        </CardContent>
      </Card>
    </div>
  );
};

// Thursday - Legs & Core
const thursdayExercises = [
  {
    name: "Barbell Back Squats",
    image: "/images/barbell-back-squats.gif",
    sets: "4",
    reps: "8-10",
    rest: "3-4 minutes",
    description: "Quad/glute foundation, full depth.",
    muscles: ["Quadriceps", "Glutes", "Hamstrings", "Core"],
    properForm: ["Chest up", "Knees track over toes", "Depth to parallel"]
  },
  {
    name: "Romanian Deadlifts",
    image: "/images/romanian-deadlift.webp",
    sets: "4",
    reps: "8-10",
    rest: "2-3 minutes",
    description: "Hamstring/glute focus, hip hinge.",
    muscles: ["Hamstrings", "Glutes", "Lower Back", "Core"],
    properForm: ["Hip hinge", "Bar close to legs", "Neutral spine"]
  },
  {
    name: "Bulgarian Split Squats",
    image: "/images/profile.jpg",
    sets: "3",
    reps: "10-12 each",
    rest: "90 seconds",
    description: "Unilateral strength, stability.",
    muscles: ["Quadriceps", "Glutes", "Hamstrings", "Core"],
    properForm: ["Upright torso", "Front knee tracks", "Controlled descent"]
  },
  {
    name: "Leg Press",
    image: "/images/profile.jpg",
    sets: "3",
    reps: "12-15",
    rest: "90 seconds",
    description: "Quad isolation, high volume.",
    muscles: ["Quadriceps", "Glutes"],
    properForm: ["Full depth", "Do not lock knees", "Controlled tempo"]
  },
  {
    name: "Leg Curls",
    image: "/images/profile.jpg",
    sets: "3",
    reps: "12-15",
    rest: "90 seconds",
    description: "Hamstring isolation.",
    muscles: ["Hamstrings"],
    properForm: ["Full squeeze", "Slow eccentric", "No hip lift"]
  },
  {
    name: "Calf Raises",
    image: "/images/profile.jpg",
    sets: "4",
    reps: "15-20",
    rest: "90 seconds",
    description: "Calf mass and strength.",
    muscles: ["Calves"],
    properForm: ["Full ROM", "Pause at top", "Slow lower"]
  },
  {
    name: "Plank Variations",
    image: "/images/plank.gif",
    sets: "3",
    reps: "45-60 seconds",
    rest: "60 seconds",
    description: "Core stability, isometric hold.",
    muscles: ["Core", "Shoulders", "Glutes"],
    properForm: ["Straight line head to heels", "Core tight"]
  },
  {
    name: "Russian Twists",
    image: "/images/profile.jpg",
    sets: "3",
    reps: "20-30",
    rest: "60 seconds",
    description: "Obliques, rotational strength.",
    muscles: ["Obliques", "Core"],
    properForm: ["Rotate shoulders", "Touch both sides", "Controlled movement"]
  },
  {
    name: "Hanging Leg Raises",
    image: "/images/profile.jpg",
    sets: "3",
    reps: "10-15",
    rest: "90 seconds",
    description: "Lower abs, hip flexors.",
    muscles: ["Abs", "Hip Flexors"],
    properForm: ["No swing", "Tuck pelvis", "Slow lower"]
  },
  {
    name: "Dead Bug",
    image: "/images/profile.jpg",
    sets: "3",
    reps: "10-12 each",
    rest: "60 seconds",
    description: "Core stability, anti-extension.",
    muscles: ["Core"],
    properForm: ["Lower back flat", "Controlled contralateral movement", "Breathe"]
  }
];

const ThursdayWorkout = () => {
  const theme = useTheme();

  const { svgMuscles } = getCombinedWorkoutMuscles(thursdayExercises);

  return (
    <div>
      <Typography variant="h5" sx={{ mb: 2, color: theme.palette.primary.main, fontWeight: 'bold' }}>
        Thursday - Legs & Core
      </Typography>

      <Card sx={{ mb: 3, backgroundColor: theme.palette.mode === 'dark' ? '#2a2a2a' : '#f8f9fa' }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, color: theme.palette.primary.main }}>
            📋 Session Overview
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Duration:</strong> 60-75 minutes
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Focus:</strong> Lower Body Strength & Core Stability
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Intensity:</strong> Moderate-High (6-12 reps)
          </Typography>
          <Typography variant="body2">
            <strong>Equipment:</strong> Barbell, rack, bench, floor mat
          </Typography>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3, backgroundColor: theme.palette.mode === 'dark' ? '#2a2a2a' : '#f8f9fa' }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, color: theme.palette.primary.main }}>
            🔥 Dynamic Warm-up (8-10 minutes)
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            • 5 minutes light cardio
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            • Hip/ankle mobility and activation
          </Typography>
          <Typography variant="body2">
            • Movement prep: bodyweight squats and hip hinge drills
          </Typography>
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {thursdayExercises.map((exercise, index) => (
          <Grid item xs={12} md={6} key={index}>
            <ExerciseCard exercise={exercise} />
          </Grid>
        ))}
      </Grid>

      <Card sx={{ mt: 3, mb: 3, backgroundColor: theme.palette.mode === 'dark' ? '#2a2a2a' : '#f0f8ff' }}>
        <CardContent>
          <Typography variant="h6" sx={{ color: theme.palette.primary.main, mb: 2 }}>
            💪 Muscles Targeted Today
          </Typography>
          <Box sx={{ mt: 2 }}>
            <MuscleHighlighter
              primaryMuscles={svgMuscles.primary}
              secondaryMuscles={svgMuscles.secondary}
              showTooltips={true}
              size="medium"
              exercises={thursdayExercises}
            />
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ mt: 3, backgroundColor: theme.palette.mode === 'dark' ? '#2a2a2a' : '#f0f8ff' }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, color: theme.palette.primary.main }}>
            🧘 Cool-down (5-10 minutes)
          </Typography>
          <Typography variant="body2">
            • Stretch quads, hamstrings, glutes, and hip flexors
          </Typography>
        </CardContent>
      </Card>
    </div>
  );
};
const tuesdayExercises = [
  {
    name: "Barbell Bench Press",
    image: "/images/bench-press.gif",
    sets: "4",
    reps: "6-8",
    rest: "3-4 minutes",
    description: "Heavy compound, chest foundation.",
    muscles: ["Chest", "Anterior Deltoids", "Triceps"],
    properForm: ["Controlled descent", "Strong press to lockout", "Shoulder blades retracted"]
  },
  {
    name: "Incline Dumbbell Press",
    image: "/images/incline-dumbbell-press.gif",
    sets: "4",
    reps: "8-10",
    rest: "2-3 minutes",
    description: "Upper chest emphasis, controlled tempo.",
    muscles: ["Upper Chest", "Anterior Deltoids", "Triceps"],
    properForm: ["Bench 30-45°", "Stretch at bottom", "Press slightly inward"]
  },
  {
    name: "Weighted Dips",
    image: "/images/weighted-dips.gif",
    sets: "3",
    reps: "10-12",
    rest: "2-3 minutes",
    description: "Lower chest + triceps, add weight if possible.",
    muscles: ["Lower Chest", "Triceps", "Anterior Deltoids"],
    properForm: ["Slight forward lean", "Depth below elbows", "Controlled press up"]
  },
  {
    name: "Cable Flyes (Mid-chest)",
    image: "/images/profile.jpg",
    sets: "3",
    reps: "12-15",
    rest: "90 seconds",
    description: "Chest isolation, focus on squeeze.",
    muscles: ["Chest"],
    properForm: ["Elbows soft", "Hugging arc", "Squeeze at midline"]
  },
  {
    name: "Close-Grip Bench Press",
    image: "/images/close-grip-bench-press.gif",
    sets: "4",
    reps: "8-10",
    rest: "2-3 minutes",
    description: "Triceps mass builder, narrow grip.",
    muscles: ["Triceps", "Chest"],
    properForm: ["Grip 12-16 inches", "Elbows tucked", "Lower with control"]
  },
  {
    name: "Overhead Triceps Extension",
    image: "/images/profile.jpg",
    sets: "3",
    reps: "10-12",
    rest: "90 seconds",
    description: "Long head triceps, full stretch.",
    muscles: ["Triceps"],
    properForm: ["Elbows fixed", "Deep stretch", "Lockout under control"]
  },
  {
    name: "Diamond Push-ups",
    image: "/images/profile.jpg",
    sets: "3",
    reps: "12-15",
    rest: "90 seconds",
    description: "Triceps endurance, bodyweight.",
    muscles: ["Triceps", "Chest"],
    properForm: ["Hands diamond shape", "Elbows close", "Full ROM"]
  },
  {
    name: "Cable Triceps Pushdowns",
    image: "/images/profile.jpg",
    sets: "3",
    reps: "15-20",
    rest: "60 seconds",
    description: "Triceps pump, high volume finish.",
    muscles: ["Triceps"],
    properForm: ["Elbows pinned", "Full extension", "Slow return"]
  }
];

const TuesdayWorkout = () => {
  const theme = useTheme();

  // Get combined muscle data for Tuesday's workout
  const { svgMuscles } = getCombinedWorkoutMuscles(tuesdayExercises);

  return (
    <div>
      <Typography variant="h5" sx={{ mb: 2, color: theme.palette.primary.main, fontWeight: 'bold' }}>
        Tuesday - Chest & Triceps
      </Typography>

      {/* Session Details */}
      <Card sx={{ mb: 3, backgroundColor: theme.palette.mode === 'dark' ? '#2a2a2a' : '#f8f9fa' }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, color: theme.palette.primary.main }}>
            📋 Session Overview
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Duration:</strong> 60-75 minutes
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Focus:</strong> Chest & Triceps Hypertrophy
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Intensity:</strong> Moderate-High (8-12 reps)
          </Typography>
          <Typography variant="body2">
            <strong>Equipment:</strong> Barbell, dumbbells, bench, dip station
          </Typography>
        </CardContent>
      </Card>

      {/* Warm-up Section */}
      <Card sx={{ mb: 3, backgroundColor: theme.palette.mode === 'dark' ? '#2a2a2a' : '#f8f9fa' }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, color: theme.palette.primary.main }}>
            🔥 Dynamic Warm-up (8-10 minutes)
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            • 5 minutes light cardio
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            • Shoulder/pec mobility and activation
          </Typography>
          <Typography variant="body2">
            • Movement prep: push-ups and band rows
          </Typography>
        </CardContent>
      </Card>

      {/* Main Workout */}
      <Grid container spacing={3}>
        {tuesdayExercises.map((exercise, index) => (
          <Grid item xs={12} md={6} key={index}>
            <ExerciseCard exercise={exercise} />
          </Grid>
        ))}
      </Grid>

      {/* Muscle Visualization Section */}
      <Card sx={{ mt: 3, mb: 3, backgroundColor: theme.palette.mode === 'dark' ? '#2a2a2a' : '#f0f8ff' }}>
        <CardContent>
          <Typography variant="h6" sx={{ color: theme.palette.primary.main, mb: 2 }}>
            💪 Muscles Targeted Today
          </Typography>

          <Box sx={{ mt: 2 }}>
            <MuscleHighlighter
              primaryMuscles={svgMuscles.primary}
              secondaryMuscles={svgMuscles.secondary}
              showTooltips={true}
              size="medium"
              exercises={tuesdayExercises}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Cool-down Section */}
      <Card sx={{ mt: 3, backgroundColor: theme.palette.mode === 'dark' ? '#2a2a2a' : '#f0f8ff' }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, color: theme.palette.primary.main }}>
            🧘 Cool-down (5-10 minutes)
          </Typography>
          <Typography variant="body2">
            • 5-10 minutes stretching for chest, triceps, shoulders
          </Typography>
        </CardContent>
      </Card>
    </div>
  );
};



const SaturdayWorkout = () => (
  <section className="workout-day">
    <Box sx={{ mb: 3, p: 2, bgcolor: 'rgba(2, 136, 209, 0.08)', borderRadius: 2, border: '1px solid rgba(2, 136, 209, 0.2)' }}>
      <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', color: 'info.main', mb: 2 }}>
        Saturday - Swimming/Cardio Day <Chip label="45-60 min" size="small" sx={{ ml: 1 }} /> <Chip label="Low-Moderate" size="small" color="info" sx={{ ml: 1 }} />
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        <strong>Focus:</strong> Endurance, Recovery, Low-Impact Cardio<br/>
        <strong>Intensity:</strong> Low-Moderate
      </Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>
        Low-intensity endurance training focused on active recovery and cardiovascular health.
        This session helps flush metabolic waste while maintaining aerobic fitness.
      </Typography>
    </Box>
    
    {/* Swimming Session Structure */}
    <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
      <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center' }}>
        <PoolIcon sx={{ mr: 1, color: 'info.main' }} /> Swimming Session Structure:
      </Typography>
      
      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, color: 'info.main' }}>Warm-up (10 minutes):</Typography>
      <Box component="ul" sx={{ pl: 2, mb: 2 }}>
        <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>Easy-pace mixed strokes</Typography>
      </Box>
      
      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, color: 'info.main' }}>Main Set (25-35 minutes):</Typography>
      <Box component="ul" sx={{ pl: 2, mb: 2 }}>
        <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>Freestyle technique work</Typography>
        <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>Backstroke for posture</Typography>
        <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>Breaststroke for leg strength</Typography>
        <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>Optional: 4×50m intervals for variety</Typography>
      </Box>
      
      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, color: 'info.main' }}>Cool-down (10 minutes):</Typography>
      <Box component="ul" sx={{ pl: 2, mb: 0 }}>
        <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>Easy swimming and floating</Typography>
      </Box>
    </Paper>
    
    {/* Alternative Options */}
    <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
      <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center' }}>
        <TimerIcon sx={{ mr: 1, color: 'info.main' }} /> Alternative Options:
      </Typography>
      <Box component="ul" sx={{ pl: 2, mb: 0 }}>
        <Typography component="li" variant="body2" sx={{ mb: 1 }}>Light cycling (45-60 minutes)</Typography>
        <Typography component="li" variant="body2" sx={{ mb: 1 }}>Nature hike (60-90 minutes)</Typography>
        <Typography component="li" variant="body2">Yoga session (45 minutes)</Typography>
      </Box>
    </Paper>
    
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Alert severity="success" sx={{ mt: 1 }}>
          <Typography variant="body2">
            <strong>Recovery Focus:</strong> Keep intensity low to moderate. You should be able to maintain a conversation throughout the session.
          </Typography>
        </Alert>
      </Grid>
    </Grid>
  </section>
);

const SundayWorkout = () => (
  <section className="workout-day">
    <Box sx={{ mb: 3, p: 2, bgcolor: 'rgba(158, 158, 158, 0.08)', borderRadius: 2, border: '1px solid rgba(158, 158, 158, 0.2)' }}>
      <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', color: 'text.secondary', mb: 2 }}>
        Sunday - Complete Rest/Recovery <Chip label="30-45 min" size="small" sx={{ ml: 1 }} /> <Chip label="Very Low" size="small" sx={{ ml: 1 }} />
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        Complete rest day with optional light activities. This day is crucial for physical and mental recovery,
        allowing your body to adapt to the week's training stimulus.
      </Typography>
      
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
        <Chip label="Focus: Recovery, Mobility, Mental Health" size="small" color="info" />
        <Chip label="Intensity: Very Low" size="small" color="success" />
      </Box>
    </Box>
    
    <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
      <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center' }}>
        <SelfImprovementIcon sx={{ mr: 1, color: 'text.secondary' }} /> Recovery Options:
      </Typography>
      <Box component="ul" sx={{ pl: 2, mb: 0 }}>
        <Typography component="li" variant="body2" sx={{ mb: 1 }}>Complete rest day</Typography>
        <Typography component="li" variant="body2" sx={{ mb: 1 }}>20-30 minute gentle walk</Typography>
        <Typography component="li" variant="body2" sx={{ mb: 1 }}>Yoga or stretching session (30-45 minutes)</Typography>
        <Typography component="li" variant="body2" sx={{ mb: 1 }}>Meditation or relaxation practice</Typography>
        <Typography component="li" variant="body2" sx={{ mb: 1 }}>Meal preparation for the week</Typography>
        <Typography component="li" variant="body2">Sleep optimization (aim for 8-9 hours)</Typography>
      </Box>
    </Paper>
    
    <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center' }}>
      <SelfImprovementIcon sx={{ mr: 1, color: 'text.secondary' }} /> Recovery Focus:
    </Typography>
    
    <Grid container spacing={2}>
      <Grid item xs={12} md={4}>
        <Card elevation={2} sx={{ height: '100%', borderRadius: 2 }}>
          <CardContent>
            <Typography variant="h6" component="h4" sx={{ fontWeight: 'bold', mb: 1, display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
              Physical Recovery
              <Chip label="Rest" size="small" color="default" sx={{ ml: 1 }} />
            </Typography>
            <Typography variant="body2">
              Allow muscles to repair and adapt to training stimulus
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      
      <Grid item xs={12} md={4}>
        <Card elevation={2} sx={{ height: '100%', borderRadius: 2 }}>
          <CardContent>
            <Typography variant="h6" component="h4" sx={{ fontWeight: 'bold', mb: 1, display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
              Mental Restoration
              <Chip label="Mindfulness" size="small" color="success" sx={{ ml: 1 }} />
            </Typography>
            <Typography variant="body2">
              Reduce stress and maintain motivation for the upcoming week
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      
      <Grid item xs={12} md={4}>
        <Card elevation={2} sx={{ height: '100%', borderRadius: 2 }}>
          <CardContent>
            <Typography variant="h6" component="h4" sx={{ fontWeight: 'bold', mb: 1, display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
              Preparation
              <Chip label="Planning" size="small" color="info" sx={{ ml: 1 }} />
            </Typography>
            <Typography variant="body2">
              Plan and prepare for the upcoming training week
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      
      <Grid item xs={12}>
        <Alert severity="info" sx={{ mt: 1 }}>
          <Typography variant="body2">
            <strong>Important:</strong> Rest is not laziness - it's an essential part of your training program. Quality sleep and stress management are crucial for progress.
          </Typography>
        </Alert>
      </Grid>
    </Grid>
  </section>
);

const Workout = () => {
  const [value, setValue] = useState(0);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const theme = useTheme(); // Get the current theme

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper 
        elevation={3} 
        sx={{ 
          borderRadius: 2, 
          overflow: 'hidden',
          bgcolor: theme.palette.background.paper, 
          color: theme.palette.text.primary,
          mb: 4
        }}
      >
        {/* Header Section */}
        <Box 
          sx={{ 
            p: { xs: 2, sm: 3, md: 4 }, 
            background: theme.palette.mode === 'dark' 
              ? 'linear-gradient(45deg, #2A2A72 0%, #009FFD 100%)'
              : 'linear-gradient(45deg, #6a11cb 0%, #2575fc 100%)',
            color: '#fff',
            textAlign: 'center'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
            <FitnessCenterIcon sx={{ fontSize: { xs: 30, sm: 40, md: 50 }, mr: 2 }} />
            <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' } }}>
              Integrated 7-Day Training Plan
            </Typography>
          </Box>
          <Typography variant="subtitle1" sx={{ mb: 2, opacity: 0.9 }}>
            Comprehensive Weekly Training for Strength, Cardio & Recovery
          </Typography>
          
          <Button
            variant="contained"
            startIcon={<WhatsAppIcon />}
            href="https://wa.me/8920078542?text=Hi! I have a question about the 7-day integrated training plan."
            target="_blank"
            sx={{ 
              mb: 2, 
              bgcolor: '#25D366', 
              '&:hover': { bgcolor: '#128C7E' },
              color: 'white'
            }}
          >
            Questions / Feedback? I'm here
          </Button>
          
          <Grid container spacing={2} justifyContent="center" sx={{ mt: 2 }}>
            <Grid item xs={12} sm={4}>
              <Paper elevation={0} sx={{ p: 1, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2 }}>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <FitnessCenterIcon fontSize="small" sx={{ mr: 1 }} />
                  Progressive Overload
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Paper elevation={0} sx={{ p: 1, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2 }}>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <SpeedIcon fontSize="small" sx={{ mr: 1 }} />
                  Varied Intensity
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Paper elevation={0} sx={{ p: 1, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2 }}>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <TimerIcon fontSize="small" sx={{ mr: 1 }} />
                  Optimal Recovery
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>
        
        {/* Program Description */}
        <Box sx={{ p: { xs: 2, sm: 3 }, borderBottom: 1, borderColor: 'divider' }}>
          <Alert 
            severity="info" 
            icon={<InfoIcon />}
            sx={{ 
              mb: 2,
              '& .MuiAlert-message': { width: '100%' }
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
              About This Program
            </Typography>
            <Typography variant="body2">
              This integrated 4-day training plan combines strength training, boxing, and cardiovascular exercise.
              Each day targets different aspects of fitness while ensuring proper recovery and progression throughout the week.
            </Typography>
          </Alert>
        </Box>

        {/* Tabs Navigation */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={value} 
            onChange={handleChange} 
            aria-label="workout tabs" 
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTab-root': {
                py: 2,
                fontWeight: 'bold',
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                minWidth: { xs: 120, sm: 140 },
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: theme.palette.action.hover
                }
              },
              '& .Mui-selected': {
                color: theme.palette.mode === 'dark' ? '#90caf9' : '#1976d2',
              },
              '& .MuiTabs-indicator': {
                height: 3,
                borderRadius: '3px 3px 0 0'
              }
            }}
          >
            <Tab 
              label={<Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <SportsMma sx={{ mr: 0.5, fontSize: '1rem' }} />
                  <Typography variant="caption" sx={{ fontWeight: 'bold' }}>MON</Typography>
                </Box>
                <Chip size="small" label="Boxing" color="secondary" sx={{ fontSize: '0.65rem', height: 20 }} />
              </Box>} 
              {...a11yProps(0)} 
            />
            <Tab 
              label={<Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <FitnessCenterIcon sx={{ mr: 0.5, fontSize: '1rem' }} />
                  <Typography variant="caption" sx={{ fontWeight: 'bold' }}>TUE</Typography>
                </Box>
                <Chip size="small" label="Chest & Triceps" color="primary" sx={{ fontSize: '0.65rem', height: 20 }} />
              </Box>} 
              {...a11yProps(1)} 
            />
            <Tab 
              label={<Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <FitnessCenterIcon sx={{ mr: 0.5, fontSize: '1rem' }} />
                  <Typography variant="caption" sx={{ fontWeight: 'bold' }}>WED</Typography>
                </Box>
                <Chip size="small" label="Back & Biceps" color="error" sx={{ fontSize: '0.65rem', height: 20 }} />
              </Box>} 
              {...a11yProps(2)} 
            />
            <Tab 
              label={<Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <FitnessCenterIcon sx={{ mr: 0.5, fontSize: '1rem' }} />
                  <Typography variant="caption" sx={{ fontWeight: 'bold' }}>THU</Typography>
                </Box>
                <Chip size="small" label="Legs & Core" color="warning" sx={{ fontSize: '0.65rem', height: 20 }} />
              </Box>} 
              {...a11yProps(3)} 
            />
            <Tab 
              label={<Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <FitnessCenterIcon sx={{ mr: 0.5, fontSize: '1rem' }} />
                  <Typography variant="caption" sx={{ fontWeight: 'bold' }}>FRI</Typography>
                </Box>
                <Chip size="small" label="Shoulders & Arms" color="success" sx={{ fontSize: '0.65rem', height: 20 }} />
              </Box>} 
              {...a11yProps(4)} 
            />
            <Tab 
              label={<Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <PoolIcon sx={{ mr: 0.5, fontSize: '1rem' }} />
                  <Typography variant="caption" sx={{ fontWeight: 'bold' }}>SAT</Typography>
                </Box>
                <Chip size="small" label="Swimming" color="info" sx={{ fontSize: '0.65rem', height: 20 }} />
              </Box>} 
              {...a11yProps(5)} 
            />
            <Tab 
              label={<Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <SelfImprovementIcon sx={{ mr: 0.5, fontSize: '1rem' }} />
                  <Typography variant="caption" sx={{ fontWeight: 'bold' }}>SUN</Typography>
                </Box>
                <Chip size="small" label="Rest" color="default" sx={{ fontSize: '0.65rem', height: 20 }} />
              </Box>} 
              {...a11yProps(6)} 
            />
          </Tabs>
        </Box>
        
        {/* Tab Content */}
        <Box sx={{ bgcolor: theme.palette.background.default }}>
          <TabPanel value={value} index={0}>
            <MondayWorkout />
          </TabPanel>
          <TabPanel value={value} index={1}>
            <TuesdayWorkout />
          </TabPanel>
          <TabPanel value={value} index={2}>
            <WednesdayWorkout />
          </TabPanel>
          <TabPanel value={value} index={3}>
            <ThursdayWorkout />
          </TabPanel>
          <TabPanel value={value} index={4}>
            <FridayWorkout />
          </TabPanel>
          <TabPanel value={value} index={5}>
            <SaturdayWorkout />
          </TabPanel>
          <TabPanel value={value} index={6}>
            <SundayWorkout />
          </TabPanel>
        </Box>
      </Paper>
    </Container>
  );
};

export default Workout;